'use client'

import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  School, Users, BookOpen, CheckCircle2, ArrowRight, Sparkles,
  ClipboardCheck, Printer, GraduationCap, ShieldCheck, Clock,
  Calculator, Menu, X, Star, PenLine
} from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import SEO from '@/components/layout/SEO'
import { useTranslation } from 'react-i18next';
import LanguagePicker from '@/components/university/ui/LanguagePicker'

// ── Tokens (school identity: deep navy + sky, distinct from homeschool emerald / church gold) ──
const S = {
  navy: '#0B1B33', navyMid: '#12294A', sky: '#2E90FA', skySoft: '#5BA9FB',
  white: '#FFFFFF', text: '#1A2433', slate: '#475569', muted: '#94A3B8',
  border: '#E2E8F0', cream: '#F8FAFC', green: '#10B981',
}

// ── Pricing config (single source of truth for this page) ──
const PRICE_PER_SEAT = 17
const MIN_SEATS = 10
const TERM_6 = 6, TERM_12 = 12

// Live price rows fallback — 12-month includes a real discount ($189 vs $204) earning the BEST VALUE badge.
// When subscription_plans has 'school_seat_6m' / 'school_seat_1y' rows, they will override these values dynamically.
const FALLBACK_PRICING = {
  price_per_seat_monthly: PRICE_PER_SEAT,
  six_month: { total_per_seat: PRICE_PER_SEAT * TERM_6, per_month: PRICE_PER_SEAT, label: '6-Month Term' },
  twelve_month: { total_per_seat: 189, per_month: 15.75, label: '12-Month Term' },
}

function fmt(n: number) { return n.toLocaleString('en-US') }

export default function SchoolsLandingPage() {
  const { t } = useTranslation();

  const navigate = useNavigate()
  const [seats, setSeats] = useState(MIN_SEATS)
  const [term, setTerm] = useState<'six_month' | 'twelve_month'>('twelve_month')
  const [menuOpen, setMenuOpen] = useState(false)
  const [pricing, setPricing] = useState(FALLBACK_PRICING)
  const [courses, setCourses] = useState<number | null>(null)

  // Live catalog count — the number on this page should be the DB's truth, not a hardcoded digit
  useEffect(() => {
    supabase
      .from('classes')
      .select('id', { count: 'exact', head: true })
      .eq('is_active', true)
      .in('facility_id', ['00000000-0000-0000-0000-000000000001', '00000000-0000-0000-0000-000000000002'])
      .then(({ count }) => { if (count) setCourses(count) })
  }, [])

  // Dynamic pricing sync with subscription_plans (admin-managed pricing pattern)
  useEffect(() => {
    async function fetchSchoolPricing() {
      try {
        const { data } = await supabase
          .from('subscription_plans')
          .select('plan_key, price_monthly, price_six_monthly, price_yearly')
          .in('plan_key', ['school_seat_1m', 'school_seat_6m', 'school_seat_1y'])

        if (data && data.length > 0) {
          setPricing(prev => {
            const p = { ...prev }
            data.forEach((row: any) => {
              const m = Number(row.price_monthly) || 0
              const s6 = Number(row.price_six_monthly) || 0
              const y = Number(row.price_yearly) || 0
              if (row.plan_key === 'school_seat_1m' && m > 0) {
                p.price_per_seat_monthly = m
              }
              if (row.plan_key === 'school_seat_6m' && (s6 > 0 || m > 0)) {
                const total = s6 > 0 ? s6 : m * TERM_6
                p.six_month = { total_per_seat: total, per_month: Math.round(total / TERM_6), label: '6-Month Term' }
              }
              if (row.plan_key === 'school_seat_1y' && (y > 0 || m > 0)) {
                const total = y > 0 ? y : (s6 > 0 ? s6 * 2 : m * TERM_12)
                p.twelve_month = { total_per_seat: total, per_month: Number((total / TERM_12).toFixed(2)), label: '12-Month Term' }
              }
            })
            return p
          })
        }
      } catch (err) {
        console.warn('Unable to load subscription_plans for schools, using fallback pricing:', err)
      }
    }
    fetchSchoolPricing()
  }, [])

  const catalogSize = courses ?? 114
  const activePlanConfig = term === 'six_month' ? pricing.six_month : pricing.twelve_month
  const termMonths = term === 'six_month' ? TERM_6 : TERM_12
  const perSeatTerm = activePlanConfig.total_per_seat
  const perMonthRate = activePlanConfig.per_month
  const perMonth = seats * perMonthRate
  const termTotal = seats * perSeatTerm

  // ── Sections data ──
  const steps = [
    { num: '01', icon: <School size={22} />, title: 'Register your school', desc: 'Create your school hub in two minutes. No installation, no IT department, no training week.' },
    { num: '02', icon: <Users size={22} />, title: 'Invite your teachers', desc: 'Teachers receive a magic link and land in their own classroom dashboard — scoped to their classes only.' },
    { num: '03', icon: <BookOpen size={22} />, title: `Pick from ${catalogSize} audited courses`, desc: 'Every course arrives complete: structured modules, lesson objectives, mastery quizzes, and written assignments with rubric feedback. Nothing to build.' },
    { num: '04', icon: <GraduationCap size={22} />, title: 'Students start learning', desc: 'Each student gets a personal AI tutor through the full curriculum — while you keep every lever of control.' },
  ]

  const pillars = [
    {
      icon: <BookOpen size={24} />,
      title: 'Curriculum that arrives finished',
      desc: `Still Waters Academy ships ${catalogSize} audited courses — from foundational Maths and Sciences to Literature, Philosophy, and World History. Each carries 20+ hours of structured lessons with defined objectives, mastery-based assessment at 80%, and rubric-graded written work. Teachers select courses from the catalog; they never generate or assemble content.`,
      points: [
        `${catalogSize} courses across every core subject`,
        'Every lesson objective and concept published up front',
        'Mastery quizzes generated from the taught concepts',
        'Written assignments with structured rubric feedback',
      ],
    },
    {
      icon: <ClipboardCheck size={24} />,
      title: 'The teacher stays in command',
      desc: "The AI tutor delivers 1:1 instruction. Your teachers provide the teaching judgment. Every AI assessment can be reviewed, and any grade can be overridden — the teacher's score sits beside the AI's, on the record, permanently. Nothing silently rewrites.",
      points: [
        'Review every quiz score and written assignment',
        'Override any grade — original always preserved',
        'Leave feedback students see in their next lesson',
        'Track strengths and flagged struggles per student',
      ],
    },
    {
      icon: <Printer size={24} />,
      title: 'Evidence for every stakeholder',
      desc: 'One click prints a complete, evidence-backed record per student: every lesson with its objective, every score with its date, retakes visible, written work with feedback, teacher comments, and the mastery summary. Term, semester, or full year — filter and print.',
      points: [
        'Term / semester / year reports in one click',
        'Retakes and grade overrides fully visible',
        'Teacher feedback printed on the record',
        'Signature-ready format for parents and inspectors',
      ],
    },
  ]

  const faqs = [
    { q: 'How is Still Waters Academy different from other classroom software?', a: 'Most classroom tools manage logistics — Still Waters Academy teaches. Each student works with a personal AI tutor through structured, audited curriculum with mastery-based assessment, while your teachers review progress, override grades, and stay in command of every classroom decision.' },
    { q: 'Do teachers need training to use it?', a: 'The teacher workflow is four steps: invite, pick courses from the catalog, assign students, review progress. Most teachers are running their first class within 15 minutes of receiving their invite.' },
    { q: 'Can we see the curriculum before committing?', a: 'Yes — every course publishes its complete scope: every module, lesson, objective, and the concepts taught. Your teachers can review any course outline in full before a single student enrols. Plus our free Prep subjects (critical thinking, study skills, and more) are available to try with no account at stillwatersacademy.com/prep.' },
    { q: 'What if the AI gets a grade wrong?', a: "Teachers can override any grade. The override is recorded beside the original AI score — never instead of it — so the assessment record always shows both the machine's judgment and the teacher's. Your teachers' authority is the final word." },
    { q: 'How does student data work?', a: 'Student records belong to your school. Progress data, assessments, and reports are scoped to your school’s workspace, and teachers only ever see their own classes. We are piloting with founding classrooms now — data processing agreements are reviewed with each school before onboarding.' },
    { q: 'What does a seat include?', a: 'One seat is one student, with unlimited access to their assigned courses, the AI tutor, all assessments, written assignments with grading, and their full progress record. Teachers and administrators are included at no charge — you only pay for students.' },
  ]

  return (
    <div className="min-h-screen" style={{ background: S.cream, fontFamily: "'Inter', system-ui, sans-serif" }}>
      <SEO
        title="Still Waters Academy for Schools — AI Tutoring with Teachers in Command"
        description="Every student gets a personal AI tutor through audited curriculum. Teachers review every assessment and override any grade. One-click term reports. From $17/seat."
        url="https://stillwatersacademy.com/schools"
      />

      {/* ── NAV ── */}
      <nav className="sticky top-0 z-50 py-3 px-4 md:px-6 flex justify-between items-center border-b backdrop-blur-xl"
        style={{ background: `${S.navy}F2`, borderColor: 'rgba(255,255,255,0.08)' }}>
        <Link to="/" className="flex items-center gap-2.5 min-w-0">
          <div className="w-8 h-8 md:w-9 md:h-9 rounded-xl flex items-center justify-center shadow-lg shrink-0" style={{ background: S.sky }}>
            <span className="text-white font-black text-base md:text-lg italic">S</span>
          </div>
          <div className="flex flex-col min-w-0">
            <span className="font-bold text-base md:text-lg text-white leading-none whitespace-nowrap">Still Waters Academy</span>
            <span className="text-[8px] md:text-[9px] uppercase tracking-[0.2em] md:tracking-[0.25em] font-black whitespace-nowrap" style={{ color: S.skySoft }}>{t('school.for_schools')}</span>
          </div>
        </Link>

        <div className="hidden md:flex items-center gap-6 text-sm font-bold text-white/50">
          <LanguagePicker />
          <a href="#how" className="hover:text-white transition-colors uppercase tracking-widest text-[10px]">{t('school.how_it_works')}</a>
          <a href="#pillars" className="hover:text-white transition-colors uppercase tracking-widest text-[10px]">Why Still Waters</a>
          <a href="#pricing" className="hover:text-white transition-colors uppercase tracking-widest text-[10px]">Pricing</a>
          <a href="#faq" className="hover:text-white transition-colors uppercase tracking-widest text-[10px]">FAQ</a>
          <Link to="/prep" className="hover:text-white transition-colors uppercase tracking-widest text-[10px]">{t('school.free_trial')}</Link>
          <button onClick={() => navigate('/education-signup?hub=school')}
            className="px-5 py-2.5 rounded-full font-black text-[10px] tracking-widest uppercase text-white shadow-lg transition-transform hover:scale-105"
            style={{ background: S.sky }}>
            Request Setup →
          </button>
        </div>

        <button className="md:hidden flex items-center justify-center w-9 h-9 rounded-lg border border-white/10 text-white shrink-0 ml-2" onClick={() => setMenuOpen(o => !o)} aria-label="Toggle navigation menu">
          {menuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden z-40 border-b border-white/10" style={{ background: S.navy }}>
            <div className="px-6 py-5 flex flex-col gap-4">
              {[
                { label: 'How it works', href: '#how' }, { label: 'Why Still Waters', href: '#pillars' },
                { label: 'Pricing', href: '#pricing' }, { label: 'FAQ', href: '#faq' },
                { label: 'Free trial', href: '/prep' },
              ].map(l => (
                <a key={l.label} href={l.href} onClick={() => setMenuOpen(false)}
                  className="text-white/80 font-bold uppercase tracking-widest text-xs py-1">{l.label}</a>
              ))}
              <div className="py-2 border-t border-b border-white/10">
                <LanguagePicker />
              </div>
              <button onClick={() => { setMenuOpen(false); navigate('/education-signup?hub=school'); }}
                className="mt-2 px-5 py-3 rounded-full font-black text-xs tracking-widest uppercase text-white" style={{ background: S.sky }}>
                Request Classroom Setup →
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── HERO ── */}
      <section className="relative px-6 pt-16 pb-20 overflow-hidden" style={{ background: S.navy }}>
        <div className="absolute inset-0 opacity-50 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 70% -10%, rgba(46,144,250,0.25) 0%, transparent 55%)' }} />

        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border font-black text-[10px] tracking-[0.2em] uppercase mb-7"
              style={{ background: 'rgba(46,144,250,0.12)', borderColor: 'rgba(46,144,250,0.3)', color: S.skySoft }}>
              <Sparkles size={12} /> Now piloting with founding classrooms
            </div>

            <h1 className="text-4xl md:text-6xl font-black text-white leading-[1.05] tracking-tight">
              Every student gets a personal tutor.<br />
              <span style={{ color: S.sky }}>{t('school.your_teachers_stay_in_command')}</span>
            </h1>

            <p className="text-lg text-white/50 max-w-xl leading-relaxed mt-6 font-medium">
              Still Waters Academy gives each student an AI tutor through {catalogSize} audited courses — with mastery-based
              assessment, rubric-graded writing, and printed term reports. Teachers review every result,
              override any grade, and keep the classroom's judgment human.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 pt-8">
              <button onClick={() => navigate('/education-signup?hub=school')}
                className="h-14 px-9 text-base font-black rounded-2xl text-white shadow-xl transition-transform hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-2"
                style={{ background: S.sky }}>
                Request Classroom Setup <ArrowRight size={18} />
              </button>
              <Link to="/prep"
                className="h-14 px-9 text-base font-black rounded-2xl border text-white hover:bg-white/5 transition-colors flex items-center justify-center gap-2"
                style={{ borderColor: 'rgba(255,255,255,0.2)' }}>
                Try it free first
              </Link>
            </div>

            <div className="pt-9 flex gap-10 border-t border-white/10 w-fit mt-2">
              {[
                { val: `${catalogSize}`, label: 'Audited courses' },
                { val: '15 min', label: 'First class running' },
                { val: `$${pricing.price_per_seat_monthly}`, label: 'Per student / month' },
              ].map((s, i) => (
                <div key={i} className="flex flex-col">
                  <span className="text-3xl font-black" style={{ color: S.skySoft }}>{s.val}</span>
                  <span className="text-[10px] uppercase tracking-[0.18em] font-black text-white/30 mt-1">{s.label}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Product panel — the printed record IS the demo */}
          <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
            className="hidden lg:block">
            <div className="rounded-[2rem] p-4 shadow-2xl" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <div className="rounded-[1.6rem] overflow-hidden" style={{ background: S.white }}>
                {/* Simulated Learning Record — light surface, matching the real artifact */}
                <div className="px-7 pt-6 pb-2 flex items-start justify-between border-b-2" style={{ borderColor: S.text }}>
                  <div>
                    <div className="text-[8px] font-black tracking-[0.22em] uppercase" style={{ color: '#A8842C' }}>Still Waters Academy · Learning Record</div>
                    <div className="text-lg font-black mt-1" style={{ fontFamily: "'Playfair Display', Georgia, serif", color: S.text }}>Biology — Term 2</div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-black" style={{ color: S.green }}>87%</div>
                    <div className="text-[8px] font-black uppercase tracking-widest" style={{ color: S.muted }}>Mastery</div>
                  </div>
                </div>
                <div className="px-7 py-4 space-y-2.5">
                  {[
                    { l: '2.1 Photosynthesis', s: 'Quiz 90% · Mar 14', o: null },
                    { l: '2.2 Cellular Respiration', s: 'Quiz 75% → retake 85%', o: null },
                    { l: '2.3 Enzymes', s: 'Essay — B+ (rubric)', o: null },
                    { l: '2.4 DNA & Replication', s: 'Quiz 88% · Mar 28', o: 'Teacher: 92% — marked for partial credit' },
                  ].map((row, i) => (
                    <div key={i} className="flex justify-between items-center py-2 px-3 rounded-lg" style={{ background: i % 2 ? 'transparent' : S.cream }}>
                      <div>
                        <div className="text-[12px] font-bold" style={{ color: S.text }}>{row.l}</div>
                        {row.o && <div className="text-[10px] font-bold mt-0.5" style={{ color: '#A8842C' }}>✏️ {row.o}</div>}
                      </div>
                      <div className="text-[11px] font-semibold" style={{ color: S.slate }}>{row.s}</div>
                    </div>
                  ))}
                  <div className="pt-2 flex items-start gap-2 px-3 py-2 rounded-lg" style={{ background: '#FBF6EA', border: '1px solid #E8D9AE' }}>
                    <PenLine size={13} style={{ color: '#A8842C', marginTop: 2 }} />
                    <div>
                      <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: '#A8842C' }}>{t('school.teacher_feedback')}</div>
                      <div className="text-[11px] mt-0.5" style={{ color: S.text }}>Strong reasoning on enzymes — watch units in calculations.</div>
                    </div>
                  </div>
                  <div className="pt-1 grid grid-cols-2 gap-4 text-[9px] font-bold text-slate-400">
                    <div className="border-t-2 border-slate-800 pt-1">Teacher — Ms. Chen</div>
                    <div className="border-t-2 border-slate-800 pt-1">Date</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute -top-16 -right-16 w-80 h-80 rounded-full blur-[110px] pointer-events-none" style={{ background: 'rgba(46,144,250,0.12)' }} />
          </motion.div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="how" className="py-20 px-6" style={{ background: S.white }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-[10px] font-black uppercase tracking-[0.3em]" style={{ color: S.sky }}>{t('school.classroom_setup')}</span>
            <h2 className="text-3xl md:text-5xl font-black mt-3 tracking-tight" style={{ color: S.text }}>A classroom running in 15 minutes</h2>
            <p className="text-slate-500 mt-4 max-w-xl mx-auto">{t('school.no_installation_no_it_project_no')}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {steps.map((step, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="p-7 rounded-2xl border transition-all hover:shadow-lg"
                style={{ background: S.cream, borderColor: S.border }}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 text-white shadow-md"
                  style={{ background: S.sky }}>
                  {step.icon}
                </div>
                <div className="text-[10px] font-black tracking-[0.2em] mb-2" style={{ color: S.muted }}>{step.num}</div>
                <h3 className="font-black text-lg mb-2" style={{ color: S.text }}>{step.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: S.slate }}>{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PILLARS ── */}
      <section id="pillars" className="py-20 px-6" style={{ background: S.cream }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-[10px] font-black uppercase tracking-[0.3em]" style={{ color: S.sky }}>Why Still Waters Academy</span>
            <h2 className="text-3xl md:text-5xl font-black mt-3 tracking-tight" style={{ color: S.text }}>
              Built for classrooms, not chatbots
            </h2>
          </div>

          <div className="space-y-6">
            {pillars.map((p, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                className="grid lg:grid-cols-[auto_1fr_1fr] gap-8 p-9 rounded-3xl border items-start"
                style={{ background: S.white, borderColor: S.border }}>
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-white shadow-lg" style={{ background: S.sky }}>
                  {p.icon}
                </div>
                <div>
                  <h3 className="text-2xl font-black mb-3 tracking-tight" style={{ color: S.text }}>{p.title}</h3>
                  <p className="text-[15px] leading-relaxed" style={{ color: S.slate }}>{p.desc}</p>
                </div>
                <ul className="space-y-3 lg:pt-9">
                  {p.points.map((pt, j) => (
                    <li key={j} className="flex items-start gap-3 text-sm font-semibold" style={{ color: S.text }}>
                      <CheckCircle2 size={17} style={{ color: S.green, marginTop: 1, flexShrink: 0 }} /> {pt}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          {/* Free trial strip */}
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            className="mt-10 p-8 rounded-3xl border-2 flex flex-col md:flex-row items-center justify-between gap-6"
            style={{ background: S.white, borderColor: `${S.green}55` }}>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(16,185,129,0.12)', color: S.green }}>
                <Star size={22} />
              </div>
              <div>
                <h3 className="font-black text-lg" style={{ color: S.text }}>Try Still Waters Academy with your students — free, no account</h3>
                <p className="text-sm mt-1 max-w-lg" style={{ color: S.slate }}>
                  Our Prep subjects — critical thinking, study skills, personal finance, and more — are free for anyone.
                  Send your students to a real lesson today and see the tutoring, quizzes, and mastery flow before you commit a dollar.
                </p>
              </div>
            </div>
            <Link to="/prep"
              className="h-12 px-8 rounded-xl font-black text-sm text-white flex items-center gap-2 flex-shrink-0 transition-transform hover:scale-105"
              style={{ background: S.green }}>
              Open Free Prep <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" className="py-20 px-6 relative overflow-hidden" style={{ background: S.navy }}>
        <div className="absolute inset-0 opacity-40 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(46,144,250,0.2) 0%, transparent 60%)' }} />

        <div className="max-w-5xl mx-auto relative">
          <div className="text-center mb-12">
            <span className="text-[10px] font-black uppercase tracking-[0.3em]" style={{ color: S.skySoft }}>{t('school.simple_transparent')}</span>
            <h2 className="text-3xl md:text-5xl font-black mt-3 text-white tracking-tight">
              ${pricing.price_per_seat_monthly} per student, per month
            </h2>
            <p className="text-white/50 mt-4 max-w-xl mx-auto">
              Teachers and administrators are always free — you pay only for student seats.
              A personal AI tutor for less than a single hour of human tuition.
            </p>
          </div>

          {/* Term toggle */}
          <div className="flex justify-center mb-8">
            <div className="p-1 rounded-2xl flex gap-1" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <button onClick={() => setTerm('six_month')}
                className={`px-8 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${term === 'six_month' ? 'text-white shadow-lg' : 'text-white/40 hover:text-white/70'}`}
                style={{ background: term === 'six_month' ? S.sky : 'transparent' }}>
                6-Month Term
              </button>
              <button onClick={() => setTerm('twelve_month')}
                className={`px-8 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all flex items-center gap-2 ${term === 'twelve_month' ? 'text-white shadow-lg' : 'text-white/40 hover:text-white/70'}`}
                style={{ background: term === 'twelve_month' ? S.sky : 'transparent' }}>
                12-Month Term
                <span className="px-2 py-0.5 rounded-full text-[9px] font-black" style={{ background: S.green, color: S.white }}>{t('school.best_value')}</span>
              </button>
            </div>
          </div>

          {/* Calculator card */}
          <div className="rounded-3xl p-8 md:p-10 shadow-2xl" style={{ background: S.white }}>
            <div className="grid md:grid-cols-2 gap-10">
              {/* Left: seat selector */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Users size={16} style={{ color: S.sky }} />
                  <label className="text-[11px] font-black uppercase tracking-[0.15em]" style={{ color: S.muted }}>{t('school.student_seats')}</label>
                </div>
                <div className="flex items-center gap-4 mt-4">
                  <button onClick={() => setSeats(Math.max(MIN_SEATS, seats - 5))}
                    className="w-12 h-12 rounded-xl text-2xl font-black border-2 transition-colors hover:border-sky-400"
                    style={{ borderColor: S.border, color: S.text, background: S.cream }}>−</button>
                  <input
                    type="number" min={MIN_SEATS} max={2000} value={seats}
                    onChange={e => setSeats(Math.max(MIN_SEATS, Math.min(2000, parseInt(e.target.value) || MIN_SEATS)))}
                    className="w-28 h-12 text-center text-2xl font-black rounded-xl border-2 outline-none"
                    style={{ borderColor: S.border, color: S.text, background: S.cream }} />
                  <button onClick={() => setSeats(seats + 5)}
                    className="w-12 h-12 rounded-xl text-2xl font-black border-2 transition-colors hover:border-sky-400"
                    style={{ borderColor: S.border, color: S.text, background: S.cream }}>+</button>
                </div>
                <p className="text-xs mt-3" style={{ color: S.muted }}>
                  Minimum {MIN_SEATS} seats. Every seat includes the full curriculum, AI tutoring, all assessments, and printed records.
                </p>

                <div className="mt-7 space-y-3">
                  {[
                    'All courses in the catalog',
                    'Personal AI tutor per student',
                    'Mastery quizzes + rubric-graded writing',
                    'Teacher grade overrides + feedback',
                    'One-click term reports',
                    'Teachers & admins — free',
                  ].map((f, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm font-semibold" style={{ color: S.text }}>
                      <CheckCircle2 size={16} style={{ color: S.green }} /> {f}
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: the math */}
              <div className="rounded-2xl p-7 flex flex-col justify-between" style={{ background: S.cream, border: `1px solid ${S.border}` }}>
                <div>
                  <div className="flex justify-between items-baseline mb-6">
                    <span className="text-[11px] font-black uppercase tracking-[0.15em]" style={{ color: S.muted }}>
                      {term === 'six_month' ? '6-Month Term' : '12-Month Term'}
                    </span>
                    <span className="text-[11px] font-bold" style={{ color: S.muted }}>${perMonthRate}/seat/mo</span>
                  </div>
                  <div className="space-y-3 text-sm" style={{ color: S.slate }}>
                    <div className="flex justify-between"><span>Seats</span><span className="font-bold" style={{ color: S.text }}>{seats}</span></div>
                    <div className="flex justify-between"><span>{t('school.per_seat_per_term')}</span><span className="font-bold" style={{ color: S.text }}>${fmt(perSeatTerm)}</span></div>
                    <div className="flex justify-between"><span>{t('school.monthly_equivalent')}</span><span className="font-bold" style={{ color: S.text }}>${fmt(perMonth)}</span></div>
                    <div className="border-t pt-3 mt-3 flex justify-between items-center" style={{ borderColor: S.border }}>
                      <span className="font-black" style={{ color: S.text }}>{t('school.total_estimate_for_the_term')}</span>
                      <span className="text-3xl font-black" style={{ color: S.sky }}>${fmt(termTotal)}</span>
                    </div>
                  </div>
                  <p className="text-[11px] mt-4" style={{ color: S.muted }}>
                    Invoice-billed per term · {termMonths} months · Custom terms & offline payments available
                  </p>
                </div>
                <button onClick={() => navigate(`/education-signup?hub=school&seats=${seats}&term=${term}`)}
                  className="mt-8 w-full h-14 rounded-2xl font-black text-white text-base shadow-xl transition-transform hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-2"
                  style={{ background: S.sky }}>
                  Request Setup for {seats} Seats <ArrowRight size={18} />
                </button>
              </div>
            </div>
          </div>

          <p className="text-center text-[11px] text-white/30 mt-6 max-w-lg mx-auto">
            Founding-classroom pilot: early schools get direct onboarding with our team. Talk to us about multi-class and multi-year arrangements.
          </p>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="py-20 px-6" style={{ background: S.white }}>
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-[10px] font-black uppercase tracking-[0.3em]" style={{ color: S.sky }}>Questions</span>
            <h2 className="text-3xl md:text-5xl font-black mt-3 tracking-tight" style={{ color: S.text }}>{t('school.what_administrators_ask')}</h2>
          </div>

          <div className="space-y-3">
            {faqs.map((f, i) => (
              <FaqItem key={i} q={f.q} a={f.a} defaultOpen={i === 0} />
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-sm mb-4" style={{ color: S.slate }}>{t('school.still_deciding_send_your_students_to')}</p>
            <Link to="/prep" className="font-black text-sm underline underline-offset-4" style={{ color: S.sky }}>
              stillwatersacademy.com/prep →
            </Link>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="py-24 px-6 text-center relative overflow-hidden" style={{ background: S.navy }}>
        <div className="absolute inset-0 opacity-30 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at center, rgba(46,144,250,0.25) 0%, transparent 65%)' }} />
        <div className="max-w-3xl mx-auto relative">
          <h2 className="text-4xl md:text-6xl font-black text-white tracking-tight leading-[1.05]">
            Give every student a tutor.<br />
            <span style={{ color: S.sky }}>{t('school.keep_every_judgment_human')}</span>
          </h2>
          <p className="text-white/50 text-lg mt-6 max-w-xl mx-auto leading-relaxed">
            From ${pricing.price_per_seat_monthly} per student per month. Setup in 15 minutes. Your curriculum, your teachers, your school.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
            <button onClick={() => navigate('/education-signup?hub=school')}
              className="h-16 px-10 text-lg font-black rounded-2xl text-white shadow-2xl transition-transform hover:scale-[1.03] active:scale-95 flex items-center justify-center gap-2"
              style={{ background: S.sky }}>
              Request Classroom Setup <ArrowRight size={20} />
            </button>
            <Link to="/prep"
              className="h-16 px-10 text-lg font-black rounded-2xl border text-white hover:bg-white/5 transition-colors flex items-center justify-center"
              style={{ borderColor: 'rgba(255,255,255,0.2)' }}>
              Try Free Prep
            </Link>
          </div>
          <div className="flex gap-6 items-center justify-center text-[10px] font-black text-white/25 uppercase tracking-[0.25em] mt-10">
            <span>{t('school.no_lock_in_beyond_the_term')}</span>
            <span className="w-1 h-1 rounded-full" style={{ background: S.sky }} />
            <span>{t('school.teachers_always_free')}</span>
            <span className="w-1 h-1 rounded-full" style={{ background: S.sky }} />
            <span>{t('school.cancel_or_resize_at_renewal')}</span>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="py-10 px-6 border-t flex flex-col md:flex-row justify-between items-center gap-6 max-w-7xl mx-auto"
        style={{ borderColor: 'rgba(255,255,255,0.06)', background: S.navy }}>
        <div className="flex items-center gap-3 opacity-40">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: S.sky }}>
            <span className="font-bold text-white text-sm">S</span>
          </div>
          <span className="text-[10px] font-black tracking-[0.25em] uppercase text-white">Still Waters Academy</span>
        </div>
        <div className="flex gap-8 text-[10px] font-black uppercase tracking-[0.3em] text-white/25">
          <Link to="/privacy" className="hover:text-white/60 transition-colors">Privacy</Link>
          <Link to="/terms" className="hover:text-white/60 transition-colors">Terms</Link>
          <Link to="/contact" className="hover:text-white/60 transition-colors">Contact</Link>
          <Link to="/homeschool" className="hover:text-white/60 transition-colors">{t('school.for_families')}</Link>
          <Link to="/church" className="hover:text-white/60 transition-colors">{t('school.for_churches')}</Link>
          <Link to="/whitelabel" className="hover:text-white/60 transition-colors">White-Label</Link>
        </div>
      </footer>
    </div>
  )
}

// ── FAQ accordion item ──
function FaqItem({ q, a, defaultOpen }: { q: string; a: string; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(!!defaultOpen)
  return (
    <div className="rounded-2xl overflow-hidden border transition-colors"
      style={{ borderColor: open ? `${S.sky}55` : S.border, background: open ? S.cream : S.white }}>
      <button onClick={() => setOpen(o => !o)} className="w-full p-5 flex items-center justify-between text-left gap-4">
        <span className="font-bold text-[15px]" style={{ color: S.text }}>{q}</span>
        <motion.div animate={{ rotate: open ? 45 : 0 }}
          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white"
          style={{ background: open ? S.sky : S.muted }}>
          <span className="text-lg font-black leading-none">+</span>
        </motion.div>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
            <div className="px-5 pb-5 text-sm leading-relaxed" style={{ color: S.slate }}>{a}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}